import axios from "axios";
import { getApiBaseURL } from "./apiBaseUrl";

const STORAGE_TOKEN = "webchatToken";
const STORAGE_COMPANY = "webchatCompany";
const STORAGE_SESSION = "webchatSessionMeta";

export const WEBCHAT_ERROR = Object.freeze({
  CLOSED: "webchat.closed",
  UNAVAILABLE: "webchat.unavailable",
});

export const webchatApi = axios.create({
  baseURL: getApiBaseURL(),
  headers: { "Content-Type": "application/json" },
});

webchatApi.interceptors.request.use((config) => {
  const company =
    config.headers?.Company ||
    config.headers?.company ||
    sessionStorage.getItem(STORAGE_COMPANY) ||
    "";
  if (company) {
    config.headers.Company = company;
  }
  const token = sessionStorage.getItem(STORAGE_TOKEN);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Evita refresh paralelo em rajada de 401. */
let refreshInFlight = null;

function shouldAttemptRefresh(error, original) {
  if (!original || original._webchatRetry) return false;
  const url = String(original.url || "");
  if (url.includes("/webchat/session/exchange")) return false;
  if (url.includes("/webchat/session/refresh")) return false;
  if (!sessionStorage.getItem(STORAGE_TOKEN)) return false;

  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  if (code === WEBCHAT_ERROR.CLOSED || status === 410) return false;
  // JWT expirado costuma virar 404 unavailable ou 401 invalid_token
  return (
    status === 401 ||
    code === "auth.invalid_token" ||
    (status === 404 && code === WEBCHAT_ERROR.UNAVAILABLE)
  );
}

webchatApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    if (!shouldAttemptRefresh(error, original)) {
      return Promise.reject(error);
    }

    original._webchatRetry = true;

    try {
      if (!refreshInFlight) {
        refreshInFlight = (async () => {
          const { data } = await webchatApi.post("/webchat/session/refresh", {});
          const payload = data?.data || data;
          if (payload?.token) {
            sessionStorage.setItem(STORAGE_TOKEN, payload.token);
          }
          return payload;
        })().finally(() => {
          refreshInFlight = null;
        });
      }
      await refreshInFlight;
      original.headers = original.headers || {};
      const nextToken = sessionStorage.getItem(STORAGE_TOKEN);
      if (nextToken) {
        original.headers.Authorization = `Bearer ${nextToken}`;
      }
      return webchatApi(original);
    } catch (refreshErr) {
      return Promise.reject(error);
    }
  }
);

export function setWebchatSession({ token, company, meta }) {
  if (token) sessionStorage.setItem(STORAGE_TOKEN, token);
  if (company) sessionStorage.setItem(STORAGE_COMPANY, company);
  if (meta) sessionStorage.setItem(STORAGE_SESSION, JSON.stringify(meta));
}

export function clearWebchatSession() {
  sessionStorage.removeItem(STORAGE_TOKEN);
  sessionStorage.removeItem(STORAGE_COMPANY);
  sessionStorage.removeItem(STORAGE_SESSION);
}

export function getWebchatToken() {
  return sessionStorage.getItem(STORAGE_TOKEN) || "";
}

export function getWebchatCompany() {
  return sessionStorage.getItem(STORAGE_COMPANY) || "";
}

export function getWebchatSessionMeta() {
  try {
    const raw = sessionStorage.getItem(STORAGE_SESSION);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Extrai code tipado da resposta Axios do Web Chat. */
export function getWebchatErrorCode(error) {
  const code = error?.response?.data?.code;
  return code ? String(code) : "";
}

export function isWebchatClosedError(error) {
  return getWebchatErrorCode(error) === WEBCHAT_ERROR.CLOSED;
}

export function isWebchatUnavailableError(error) {
  const code = getWebchatErrorCode(error);
  return (
    code === WEBCHAT_ERROR.UNAVAILABLE ||
    code === "auth.invalid_token" ||
    error?.response?.status === 404 ||
    error?.response?.status === 401
  );
}

export async function webchatExchange({ token, company }) {
  const { data } = await webchatApi.post(
    "/webchat/session/exchange",
    { token, company },
    { headers: { Company: company } }
  );
  return data?.data || data;
}

export async function webchatRefresh() {
  const { data } = await webchatApi.post("/webchat/session/refresh", {});
  const payload = data?.data || data;
  if (payload?.token) {
    sessionStorage.setItem(STORAGE_TOKEN, payload.token);
  }
  return payload;
}

/** Renova o JWT proativamente (sessão DB ainda válida). Ignora falhas. */
export async function ensureWebchatSessionFresh() {
  if (!getWebchatToken() || !getWebchatCompany()) return null;
  try {
    return await webchatRefresh();
  } catch {
    return null;
  }
}

export async function webchatGetMessages({ before, after, limit } = {}) {
  const { data } = await webchatApi.get("/webchat/messages", {
    params: { before, after, limit },
  });
  return data?.data || data;
}

export async function webchatPostPresence({ state, keepalive = false } = {}) {
  const token = getWebchatToken();
  const company = getWebchatCompany();
  if (!token || !company) return null;
  if (keepalive && typeof fetch === "function") {
    try {
      await fetch(`${getApiBaseURL()}/webchat/presence`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Company: company,
        },
        body: JSON.stringify({ state }),
        keepalive: true,
      });
    } catch {
      /* pagehide / hidden */
    }
    return null;
  }
  const { data } = await webchatApi.post("/webchat/presence", { state });
  return data?.data || data;
}

export async function webchatPostMessage(payload) {
  const { data } = await webchatApi.post("/webchat/messages", payload);
  return data?.data || data;
}

export async function webchatSignUpload(payload) {
  const { data } = await webchatApi.post("/webchat/uploads/sign", payload);
  return data?.data || data;
}

export async function webchatMediaDownload({ url, filename } = {}) {
  const { data } = await webchatApi.get("/webchat/media/download", {
    params: { url, filename },
  });
  return data?.data || data;
}

export async function webchatWidgetConfig({ company, key }) {
  const { data } = await webchatApi.get("/webchat/widget/config", {
    params: { key, company },
    headers: { Company: company },
  });
  return data?.data || data;
}

export async function webchatWidgetStart(payload) {
  const company = payload?.company;
  const { data } = await webchatApi.post("/webchat/widget/start", payload, {
    headers: company ? { Company: company } : {},
  });
  return data?.data || data;
}
